import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon-v2.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as ConnectIcon } from '../../assets/icons/devices-connect-icon.svg';
import { ReactComponent as EnergyIcon } from '../../assets/icons/energy-icon.svg';
// import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import endConsumerV2 from '../../utils/api/end-consumer-v2';

import ChargingSpeedSocChart from './component/ChargingSpeedSocChart';
import MeterHardwareStats from './component/MeterHardwareStats';
import DeviceSidePanel from './DeviceSidePanel';
import StationSidePanel from './StationSidePanel';

/**
 * Get exact duration between two ISO timestamps in HHh MMm format
 */
const getExactDuration = (startIso?: string, endIso?: string): string => {
  if (!startIso || !endIso) return '--';

  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();

  const diffMs = Math.max(end - start, 0);

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours)}hrs ${String(minutes).padStart(2, '0')}m`;
};

/** Props for ChargingSessionSidePanel */
interface ChargingSessionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  datatype?: string;
}

/** Live session calculated values */
interface LiveInfo {
  totalkWhConsumed: number;
  paidAmount: number;
}

/** SOC vs kW chart data point */
interface SocKwPoint {
  ts: string;
  kw: number;
  soc: number;
}

/** Vehicle information */
interface VehicleInfo {
  manufacturer: string;
  model: string;
  connectorType: string;
}

/** Wallet history */
interface WalletHistory {
  amount: string;
  paymentStatus: string;
}

/** Charging session API response */
interface ChargingSessionResponse {
  desiredChargePercentage: number;
  chargingStation: string;
  powerConsumedInKwh: string;
  transactionStartsAt: string;
  transactionEndsAt?: string;
  finalBatteryCapacity?: number;
  vehicle?: VehicleInfo;
  chargingStationName: string;
  chargingPointName: string;
  dynamicPricing?: { price: string };
  price?: string;
  connector?: number;
  chargingPointSn?: string;
  status?: string;
  socVsKW?: SocKwPoint[];
  walletHistoryObject?: WalletHistory;
  estAmount?: string;
  latitude: string;
  longitude: string;
}

/**
 * Convert lat/lng strings to valid Google map position
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * ChargingSessionSidePanel
 * Displays detailed information about a charging session
 */
function ChargingSessionSidePanel(properties: ChargingSessionSidePanelProperties): JSX.Element {
  const { t } = useTranslation('endConsumer');
  const { isLoaded } = useGoogleMaps();
  const branding = useAppBranding();
  const dispatch = useAppDispatch();
  const { fetchIdFrom, id, datatype } = properties;
  const [searchParameters] = useSearchParams();

  const [sessionData, setSessionData] = useState<ChargingSessionResponse | null>(null);
  const [liveInfo, setLiveInfo] = useState<LiveInfo>({
    totalkWhConsumed: 0,
    paidAmount: 0,
  });
  const [overlayPanel, setOverlayPanel] = useState<'closed' | 'station' | 'device'>('closed');

  const mapCenter = getValidPosition(sessionData?.latitude, sessionData?.longitude);

  /**
   * Calculates session duration and financials
   */
  const computeLiveInfo = useCallback((data: ChargingSessionResponse): void => {
    const energy = Number.parseFloat(data.powerConsumedInKwh || '0');
    const amount = Number.parseFloat(data.walletHistoryObject?.amount || data.estAmount || '0');

    setLiveInfo({
      totalkWhConsumed: energy,
      paidAmount: amount,
    });
  }, []);

  /**
   * Opens overlay side panels
   */
  const handleOpenOverlayPanel = useCallback((type: 'station' | 'device') => {
    setOverlayPanel(type);
  }, []);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  /**
   * Fetch charging session details from API
   */
  const fetchChargingSession = useCallback(
    async (chargingId: string): Promise<void> => {
      try {
        const { data } = await endConsumerV2.fetchChargingTransaction(chargingId);
        const session: ChargingSessionResponse = data;

        setSessionData(session);
        computeLiveInfo(session);
      } catch (error: unknown) {
        dispatch(
          setNotificationMessage({
            status: true,
            message: t('unableToFetchChargingSessionDetails'),
            type: 'error',
            code: 0,
          }),
        );
      }
    },
    [computeLiveInfo, dispatch],
  );

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('chargingId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }
    fetchChargingSession(stationId);
  }, [searchParameters, id]);

  /**
   * Renders overlay panel content
   */
  const renderOverlayPanel = (): JSX.Element | null => {
    if (!sessionData) return null;

    if (overlayPanel === 'station') {
      return <StationSidePanel fetchIdFrom="props" id={sessionData.chargingStation} />;
    }

    if (overlayPanel === 'device') {
      return <DeviceSidePanel fetchIdFrom="props" id={sessionData.chargingPointSn ?? ''} />;
    }

    return null;
  };

  return (
    <div className="charging-session-side-panel">
      <div className="charging-session-side-content">
        <MeterHardwareStats
          value={sessionData?.desiredChargePercentage ?? 0}
          finishedTime={
            sessionData?.transactionStartsAt && sessionData?.transactionEndsAt
              ? `${getExactDuration(sessionData.transactionStartsAt, sessionData.transactionEndsAt)}`
              : '--'
          }
          energy={`${sessionData?.powerConsumedInKwh ?? 0} kWh`}
          completed={sessionData?.status}
        />
        <div className="vehicle-ideals">
          <p className="heading">{t('vehicleName')}</p>
          <p className="values">
            {sessionData?.vehicle
              ? `${sessionData.vehicle.manufacturer} ${sessionData.vehicle.model}`
              : '--'}
          </p>
        </div>
        <div className="live-info">
          <div className="live-info__item">
            <p className="label">{t('energy')}</p>
            <p className="value">
              <EnergyIcon className="icon" />
              {liveInfo.totalkWhConsumed.toFixed(2)} kWh
            </p>
          </div>
          {datatype !== 'personal' && (
            <div className="live-info__item">
              <p className="label">{t('cost')}</p>
              <p className="value">
                <p className="icon">{branding.text.currency}</p>
                <span>{liveInfo.paidAmount.toFixed(2)}</span>
              </p>
            </div>
          )}
          <div className="live-info__item">
            <p className="label">{t('elapsedTime')}</p>
            <p className="value">
              <ClockIcon className="icon" />
              <span>
                {sessionData?.transactionStartsAt && sessionData?.transactionEndsAt
                  ? `${getExactDuration(
                      sessionData.transactionStartsAt,
                      sessionData.transactionEndsAt,
                    )}`
                  : '--'}
              </span>
            </p>
          </div>
        </div>
        <ChargingSpeedSocChart data={sessionData?.socVsKW ?? []} />
        <div className="station-devices">
          {datatype !== 'personal' && (
            <div className="content-list">
              <p className="title">{t('stationName')}</p>
              <p
                className="value"
                role="presentation"
                onClick={() => handleOpenOverlayPanel('station')}>
                {sessionData?.chargingStationName ?? '--'}
              </p>
            </div>
          )}

          <div className="content-list">
            <p className="title">{t('device')}</p>
            <p
              className="value"
              role="presentation"
              onClick={() => handleOpenOverlayPanel('device')}>
              {sessionData?.chargingPointName ?? '--'}
            </p>
          </div>
          {datatype !== 'personal' && (
            <div className="content-list">
              <p className="title">{t('chargeCostPerKwh')}</p>
              <p className="value">
                <p className="icon">{branding.text.currency}</p>
                {sessionData?.price ?? sessionData?.dynamicPricing?.price ?? 0}
              </p>
            </div>
          )}
        </div>

        {datatype !== 'personal' && isLoaded && (
          <div className="mini-map">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter || { lat: 11.0251671, lng: 77.02125625 }}
              zoom={12}
              options={miniMapOptions}>
              <Marker
                position={mapCenter || { lat: 11.0251671, lng: 77.02125625 }}
                icon={{
                  url: branding.icons.stationLocator,
                  scaledSize: new window.google.maps.Size(32, 32),
                }}
              />
            </GoogleMap>
          </div>
        )}
        <div className="card" data-testid="connector-card">
          <div className="leftSection">
            <div className="imageWrapper">
              <ConnectIcon />
              <span className="connectorType">{sessionData?.vehicle?.connectorType ?? '--'}</span>
            </div>
            <div className="details">
              <h3 className="name">
                {t('connector')} {sessionData?.connector ?? '--'}
              </h3>
              <span className="connectorId">{sessionData?.chargingPointSn ?? '--'}</span>
            </div>
          </div>
          <div className="rightSection">
            <span
              className={
                sessionData?.status === 'finished' ? 'availableBadge' : 'unavailableBadge'
              }>
              {sessionData?.status ?? t('unknown')}
            </span>
            <span className="power">{sessionData?.powerConsumedInKwh ?? 0} kWh</span>
          </div>
        </div>
      </div>
      <div className="device-side-panel-description" />
      <div
        className={`device-panel-station-view-wrap ${overlayPanel !== 'closed' ? 'active' : ''}`}>
        <div className="side-panel-header">
          <span style={{ opacity: 0.5, cursor: 'not-allowed' }}>{/* <NewTabIcon /> */}</span>
          <span role="presentation" onClick={() => setOverlayPanel('closed')}>
            <CloseIcon />
          </span>
        </div>
        <div className="side-panel-content">{renderOverlayPanel()}</div>
      </div>
    </div>
  );
}

export default ChargingSessionSidePanel;
